import { inject, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, filter, map, of, switchMap, tap, timer } from 'rxjs';

export abstract class AuthBaseService {
  protected abstract readonly storageKey: string;
  protected abstract readonly storageExpKey: string;

  protected router = inject(Router);

  protected token$ = new BehaviorSubject<string | null>(null);

  isAuthenticated$ = this.token$.pipe(map(t => t !== null));

  tokenPayload$ = this.token$.pipe(
    map(t => (t ? this.decodeToken(t) : null))
  );

  autoLogout$ = this.tokenPayload$.pipe(
    filter(p => p !== null),
    switchMap(p => {
      const ms = p!.exp * 1000 - Date.now();
      if (ms <= 0) {
        this.logout();
        return of(null);
      }
      return timer(ms).pipe(tap(() => this.logout()));
    })
  );

  get token() {
    return this.token$.getValue();
  }

  protected init() {
    const stored = localStorage.getItem(this.storageKey);
    if (stored) {
      const exp = localStorage.getItem(this.storageExpKey);
      if (!exp || parseInt(exp, 10) >= Date.now()) {
        this.token$.next(stored);
      } else {
        this.clearStorage();
      }
    }
    this.autoLogout$.subscribe();
  }

  protected storeToken(token: string, exp: number) {
    localStorage.setItem(this.storageKey, token);
    localStorage.setItem(this.storageExpKey, String(exp * 1000));
    this.token$.next(token);
  }

  protected clearStorage() {
    this.token$.next(null);
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem(this.storageExpKey);
  }

  decodeToken(token: string): { exp: number; iat: number; _id: string } | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) throw new Error('Invalid token format');
      return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }

  abstract logout(): void;
}

export const AUTH_SERVICE = new InjectionToken<AuthBaseService>('AUTH_SERVICE');
